// src/pages/Registration.tsx
import { Formik } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import BigLogo from "../assets/images/axlplLogoImg.png";
import Input from "../components/ui/input/Input";
import Label from "../components/ui/label/Label";
import SingleSelect, { OptionType } from "../components/ui/select/SingleSelect";

import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "../components/ui/button/Button";
import { InlineLogisticsLoader } from "../components/ui/spinner";
import { submitCustomerRegistration } from "../redux/slices/customerRegistrationSlice";
import { fetchNatureOfBusiness } from "../redux/slices/natureOfBusinessSlice";
import { fetchRegisterCategories } from "../redux/slices/registerCategorySlice";
import {
  fetchRegisterAreasByPincode,
  fetchRegisterPincodeDetail,
} from "../redux/slices/registerPincodeSlice";
import type { AppDispatch, RootState } from "../redux/store";
import { DropzoneField } from "../components/ui/choosefile/DropzoneField";
// ---------- Helpers ----------
const today = new Date();
today.setHours(0, 0, 0, 0);

const ACCEPTED_EXTENSIONS = ".pdf,.jpg,.jpeg,.png";

// ---- Validation ----
const RegistrationSchema = Yup.object().shape({
  companyName: Yup.string().trim().required("Company name is required"),
  fName: Yup.string().trim().required("Full name is required"),

  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^\d{10}$/, "Mobile number must be 10 digits"),

  // contact & auth (now required)
  telephone: Yup.string()
    .trim()
    .matches(/^\d{5,12}$/, "Telephone must be 5–12 digits")
    .required("Telephone is required"),
  fax: Yup.string()
    .trim()
    .matches(/^\d{5,12}$/, "Fax must be 5–12 digits")
    .required("Fax number is required"),
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d).{8,}$/,
      "Must include a letter and a number"
    ),

  // IDs & tax (now required)
  panNumber: Yup.string()
    .transform((v) => (v ? v.toUpperCase() : v))
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/i, "Invalid PAN (e.g., ABCDE1234F)")
    .required("PAN number is required"),
  gstNumber: Yup.string()
    .transform((v) => (v ? v.toUpperCase() : v))
    .matches(
      /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/i,
      "Invalid GSTIN (15 characters)"
    )
    .required("GST number is required"),

  // Insurance (now required)
  insuranceValue: Yup.string()
    .trim()
    .matches(
      /^(?:\d+)(?:\.\d{1,2})?$/,
      "Enter a valid amount (up to 2 decimals)"
    )
    .required("AXLPL insurance value is required"),
  thirdPartyInsuranceValue: Yup.string()
    .trim()
    .matches(
      /^(?:\d+)(?:\.\d{1,2})?$/,
      "Enter a valid amount (up to 2 decimals)"
    )
    .required("Third-party insurance value is required"),
  insuranceExpiryDate: Yup.string()
    .required("Insurance expiry date is required")
    .test(
      "not-past",
      "Insurance expiry date must be today or a future date",
      (val) => {
        if (!val) return false;
        const d = new Date(val);
        d.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d >= today;
      }
    ),

  // selects & address (all required)
  registerCategory: Yup.object({ value: Yup.string(), label: Yup.string() })
    .nullable()
    .required("Please select a category"),
  natureOfBusiness: Yup.object({ value: Yup.string(), label: Yup.string() })
    .nullable()
    .required("Please select nature of business"),
  addressLine1: Yup.string().trim().required("Address Line 1 is required"),
  addressLine2: Yup.string().trim().required("Address Line 2 is required"),
  pinCode: Yup.string()
    .required("Pincode is required")
    .matches(/^\d{6}$/, "Pincode must be 6 digits"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  area: Yup.object({ value: Yup.string(), label: Yup.string() })
    .nullable()
    .required("Please select area"),

  // files (now required)
  uploadPanCard: Yup.mixed<File>()
    .required("PAN card file is required")
    .test(
      "fileType",
      "Allowed types: PDF, JPG, PNG",
      (file) =>
        !!file &&
        ["application/pdf", "image/jpeg", "image/png"].includes(file.type)
    )
    .test(
      "fileSize",
      "Max size 5MB",
      (file) => !!file && file.size <= 5 * 1024 * 1024
    ),

  uploadGstCertificate: Yup.mixed<File>()
    .required("GST certificate file is required")
    .test(
      "fileType",
      "Allowed types: PDF, JPG, PNG",
      (file) =>
        !!file &&
        ["application/pdf", "image/jpeg", "image/png"].includes(file.type)
    )
    .test(
      "fileSize",
      "Max size 5MB",
      (file) => !!file && file.size <= 5 * 1024 * 1024
    ),
});

type FormValues = {
  companyName: string;
  fName: string;
  mobile: string;

  // NEW/UPDATED
  telephone: string;
  fax: string;
  email: string;
  password: string;
  panNumber: string;
  gstNumber: string;
  insuranceValue: string; // AXLPL Insurance Value
  thirdPartyInsuranceValue: string; // fixed name
  insuranceExpiryDate: string;

  registerCategory: OptionType | null;
  natureOfBusiness: OptionType | null;
  addressLine1: string;
  addressLine2: string;

  pinCode: string;
  state: string;
  city: string;
  area: OptionType | null;

  stateId?: string;
  cityId?: string;
  countryId?: string;
  uploadPanCard: File | null; // NEW
  uploadGstCertificate: File | null; // NEW
};

const Registration: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // existing selects
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useSelector((s: RootState) => s.registerCategory);
  const {
    items: nobItems,
    loading: nobLoading,
    error: nobError,
  } = useSelector((s: RootState) => s.natureOfBusiness);

  const { detailLoading, detailError, areas, areasLoading, areasError } =
    useSelector((s: RootState) => s.registerPincode);

  useEffect(() => {
    dispatch(fetchRegisterCategories());
    dispatch(fetchNatureOfBusiness());
  }, [dispatch]);

  // Map API -> react-select options
  const categoryOpts: OptionType[] = (categories ?? []).map((c) => ({
    label: c.name,
    value: c.value,
  }));
  const natureOpts: OptionType[] = (nobItems ?? []).map((n) => ({
    label: n.name,
    value: n.value,
  }));
  const areaOpts: OptionType[] = (areas ?? [])
    .filter((a) => (a.name ?? "").trim() !== "")
    .map((a) => ({ label: a.name, value: a.id }));

  const initialValues: FormValues = {
    companyName: "",
    fName: "",
    mobile: "",

    // NEW/UPDATED
    telephone: "",
    fax: "",
    email: "",
    password: "",
    panNumber: "",
    gstNumber: "",
    insuranceValue: "",
    thirdPartyInsuranceValue: "",
    insuranceExpiryDate: "",

    registerCategory: null,
    natureOfBusiness: null,
    addressLine1: "",
    addressLine2: "",
    pinCode: "",
    state: "",
    city: "",
    area: null,
    stateId: "",
    cityId: "",
    countryId: "",
    uploadPanCard: null, // NEW
    uploadGstCertificate: null, // NEW
  };

  const { loading } = useSelector((s: RootState) => s.customerRegistration);
  return (
    <section id="p_newLoginFormSection">
      <div className="container-fluid px-0">
        <div className="p_newFormRow">
          <div className="col-lg-8 col-md-8 col-12 p_newLeftSide px-0">
            <div className="p_loginInnerFormWrap p_registerInnerFormWrap">
              <div className="logoWrap d-flex justify-content-center mb-5">
                <img
                  src={BigLogo}
                  alt="axlpl"
                  width={300}
                  height={100}
                  className="img-fluid"
                />
              </div>

              <h1 className="p_formTitle">Create an account</h1>

              <Formik
                initialValues={initialValues}
                validationSchema={RegistrationSchema}
                onSubmit={async (values, { resetForm }) => {
                  try {
                    await dispatch(submitCustomerRegistration(values)).unwrap();
                    toast.success("Registration Successfully");
                    resetForm();
                  } catch (err: any) {
                    toast.error(String(err || "Registration failed"));
                  }
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  setFieldTouched,
                  setFieldError,
                  handleSubmit,
                  validateForm,
                  setTouched,
                }) => {
                  const handlePinBlur = async (
                    e: React.FocusEvent<HTMLInputElement>
                  ) => {
                    handleBlur(e);
                    const pin = e.target.value.trim();

                    if (!/^\d{6}$/.test(pin)) {
                      setFieldValue("state", "");
                      setFieldValue("city", "");
                      setFieldValue("area", null);
                      setFieldValue("stateId", "");
                      setFieldValue("cityId", "");
                      setFieldValue("countryId", "");
                      return;
                    }

                    try {
                      const d = await dispatch(
                        fetchRegisterPincodeDetail(pin)
                      ).unwrap();
                      setFieldValue("state", d.state_name);
                      setFieldValue("city", d.city_name);
                      setFieldValue("stateId", d.state_id);
                      setFieldValue("cityId", d.city_id);
                      setFieldValue("countryId", d.country_id);

                      const list = await dispatch(
                        fetchRegisterAreasByPincode(pin)
                      ).unwrap();
                      const match = list.find(
                        (a) =>
                          a.id === d.area_id && (a.name ?? "").trim() !== ""
                      );
                      if (match) {
                        setFieldValue("area", {
                          value: match.id,
                          label: match.name,
                        });
                      } else {
                        setFieldValue("area", null);
                      }
                    } catch {
                      setFieldValue("state", "");
                      setFieldValue("city", "");
                      setFieldValue("area", null);
                      setFieldValue("stateId", "");
                      setFieldValue("cityId", "");
                      setFieldValue("countryId", "");
                    }
                  };
                  const getFirstError = (obj: any): string | null => {
                    for (const v of Object.values(obj || {})) {
                      if (!v) continue;
                      if (typeof v === "string") return v;
                      if (typeof v === "object") {
                        const r = getFirstError(v);
                        if (r) return r;
                      }
                    }
                    return null;
                  };
                  const submitWithValidation = async () => {
                    const errs = await validateForm();
                    if (Object.keys(errs).length) {
                      // mark everything as touched so inline messages show
                      const allTouched = Object.keys(values).reduce(
                        (acc, k) => {
                          (acc as any)[k] = true;
                          return acc;
                        },
                        {} as Record<string, boolean>
                      );
                      setTouched(allTouched as any, true);

                      toast.error(
                        getFirstError(errs) ||
                          "Please fix the highlighted errors"
                      );
                      return;
                    }
                    handleSubmit(); // will trigger your existing onSubmit
                  };
                  return (
                    <form
                      className="p_loginMainForm"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <div className="row">
                        {/* Company Name */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={values.companyName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.companyName && errors.companyName
                                ? String(errors.companyName)
                                : undefined
                            }
                            touched={!!touched.companyName}
                            placeHolder="Enter Company Name"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="companyName"
                            text="Company Name"
                          />
                          {touched.companyName && errors.companyName && (
                            <div className="errorText">
                              {String(errors.companyName)}
                            </div>
                          )}
                        </div>

                        {/* Full Name */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="fName"
                            name="fName"
                            value={values.fName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.fName && errors.fName
                                ? String(errors.fName)
                                : undefined
                            }
                            touched={!!touched.fName}
                            placeHolder="Enter Full Name"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="fName"
                            text="Full Name"
                          />
                          {touched.fName && errors.fName && (
                            <div className="errorText">
                              {String(errors.fName)}
                            </div>
                          )}
                        </div>

                        {/* Mobile */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="mobile"
                            name="mobile"
                            value={values.mobile}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.mobile && errors.mobile
                                ? String(errors.mobile)
                                : undefined
                            }
                            touched={!!touched.mobile}
                            placeHolder="Enter 10-digit mobile number"
                            className="form-control"
                            maxLength={10}
                          />
                          <Label
                            className="form-label"
                            htmlFor="mobile"
                            text="Mobile Number"
                          />
                          {touched.mobile && errors.mobile && (
                            <div className="errorText">
                              {String(errors.mobile)}
                            </div>
                          )}
                        </div>

                        {/* Register Category */}
                        <div className="form-floating col-md-6 col-12 mb-5 newLabel">
                          <SingleSelect
                            options={categoryOpts}
                            value={values.registerCategory}
                            onChange={(option) =>
                              setFieldValue("registerCategory", option)
                            }
                            placeholder={
                              categoriesLoading
                                ? "Loading categories..."
                                : "Select category"
                            }
                            isLoading={categoriesLoading}
                            id="registerCategorySelectOpt"
                            hasError={
                              !!touched.registerCategory &&
                              !!errors.registerCategory
                            }
                          />
                          <Label
                            className="form-label"
                            htmlFor="registerCategory"
                            text="Select Category"
                          />
                          {touched.registerCategory &&
                            errors.registerCategory && (
                              <div className="errorText">
                                {typeof errors.registerCategory === "string"
                                  ? errors.registerCategory
                                  : "Please select a category"}
                              </div>
                            )}
                          {categoriesError && (
                            <div className="errorText mt-1">
                              Failed to load categories: {categoriesError}
                            </div>
                          )}
                        </div>

                        {/* Nature of Business */}
                        <div className="form-floating col-md-6 col-12 mb-5 newLabel">
                          <SingleSelect
                            options={natureOpts}
                            value={values.natureOfBusiness}
                            onChange={(option) =>
                              setFieldValue("natureOfBusiness", option)
                            }
                            placeholder={
                              nobLoading
                                ? "Loading nature of business..."
                                : "Select nature of business"
                            }
                            isLoading={nobLoading}
                            id="registerCategorySelectOpt"
                            hasError={
                              !!touched.natureOfBusiness &&
                              !!errors.natureOfBusiness
                            }
                          />
                          <Label
                            className="form-label"
                            htmlFor="natureOfBusiness"
                            text="Select Nature of Business"
                          />
                          {touched.natureOfBusiness &&
                            errors.natureOfBusiness && (
                              <div className="errorText">
                                {typeof errors.natureOfBusiness === "string"
                                  ? errors.natureOfBusiness
                                  : "Please select nature of business"}
                              </div>
                            )}
                          {nobError && (
                            <div className="errorText mt-1">
                              Failed to load nature of business: {nobError}
                            </div>
                          )}
                        </div>

                        {/* Pincode */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="pinCode"
                            name="pinCode"
                            value={values.pinCode}
                            onChange={handleChange}
                            onBlur={handlePinBlur}
                            error={
                              touched.pinCode && errors.pinCode
                                ? String(errors.pinCode)
                                : undefined
                            }
                            touched={!!touched.pinCode}
                            placeHolder="Enter 6-digit pincode"
                            className="form-control"
                            maxLength={6}
                          />
                          <Label
                            className="form-label"
                            htmlFor="pinCode"
                            text="Pincode"
                          />
                          {touched.pinCode && errors.pinCode && (
                            <div className="errorText">
                              {String(errors.pinCode)}
                            </div>
                          )}
                          {(detailLoading || areasLoading) && (
                            <div className="small text-muted mt-1">
                              Fetching location…
                            </div>
                          )}
                          {(detailError || areasError) && (
                            <div className="errorText mt-1">
                              {detailError || areasError}
                            </div>
                          )}
                        </div>

                        {/* State (read-only) */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="state"
                            name="state"
                            value={values.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.state && errors.state
                                ? String(errors.state)
                                : undefined
                            }
                            touched={!!touched.state}
                            placeHolder="State"
                            className="form-control"
                            readOnly
                          />
                          <Label
                            className="form-label"
                            htmlFor="state"
                            text="State"
                          />
                          {touched.state && errors.state && (
                            <div className="errorText">
                              {String(errors.state)}
                            </div>
                          )}
                        </div>

                        {/* Area (value = id, label = name) */}
                        <div className="form-floating col-md-6 col-12 mb-5 newLabel">
                          <SingleSelect
                            options={areaOpts}
                            value={values.area}
                            onChange={(option) => setFieldValue("area", option)}
                            placeholder={
                              areasLoading ? "Loading areas..." : "Select area"
                            }
                            isLoading={areasLoading}
                            id="registerCategorySelectOpt"
                            hasError={!!touched.area && !!errors.area}
                          />
                          <Label
                            className="form-label"
                            htmlFor="area"
                            text="Select Area"
                          />
                          {touched.area && errors.area && (
                            <div className="errorText">
                              {typeof errors.area === "string"
                                ? errors.area
                                : "Please select area"}
                            </div>
                          )}
                          {areasError && (
                            <div className="errorText mt-1">
                              Failed to load areas: {areasError}
                            </div>
                          )}
                        </div>

                        {/* City (read-only) */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="city"
                            name="city"
                            value={values.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.city && errors.city
                                ? String(errors.city)
                                : undefined
                            }
                            touched={!!touched.city}
                            placeHolder="City"
                            className="form-control"
                            readOnly
                          />
                          <Label
                            className="form-label"
                            htmlFor="city"
                            text="City"
                          />
                          {touched.city && errors.city && (
                            <div className="errorText">
                              {String(errors.city)}
                            </div>
                          )}
                        </div>

                        {/* Address Line 1 */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="addressLine1"
                            name="addressLine1"
                            value={values.addressLine1}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.addressLine1 && errors.addressLine1
                                ? String(errors.addressLine1)
                                : undefined
                            }
                            touched={!!touched.addressLine1}
                            placeHolder="Enter Address Line 1"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="addressLine1"
                            text="Address Line 1"
                          />
                          {touched.addressLine1 && errors.addressLine1 && (
                            <div className="errorText">
                              {String(errors.addressLine1)}
                            </div>
                          )}
                        </div>

                        {/* Address Line 2 */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="addressLine2"
                            name="addressLine2"
                            value={values.addressLine2}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.addressLine2 && errors.addressLine2
                                ? String(errors.addressLine2)
                                : undefined
                            }
                            touched={!!touched.addressLine2}
                            placeHolder="Enter Address Line 2"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="addressLine2"
                            text="Address Line 2"
                          />
                          {touched.addressLine2 && errors.addressLine2 && (
                            <div className="errorText">
                              {String(errors.addressLine2)}
                            </div>
                          )}
                        </div>

                        {/* Hidden ID fields */}
                        <input
                          type="hidden"
                          name="stateId"
                          value={values.stateId}
                        />
                        <input
                          type="hidden"
                          name="cityId"
                          value={values.cityId}
                        />
                        <input
                          type="hidden"
                          name="countryId"
                          value={values.countryId}
                        />

                        {/* --- NEW FIELDS --- */}

                        {/* Telephone Number */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="telephone"
                            name="telephone"
                            value={values.telephone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.telephone && errors.telephone
                                ? String(errors.telephone)
                                : undefined
                            }
                            touched={!!touched.telephone}
                            placeHolder="Enter telephone number"
                            className="form-control"
                            maxLength={12}
                          />
                          <Label
                            className="form-label"
                            htmlFor="telephone"
                            text="Telephone Number"
                          />
                          {touched.telephone && errors.telephone && (
                            <div className="errorText">
                              {String(errors.telephone)}
                            </div>
                          )}
                        </div>

                        {/* Fax Number */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="fax"
                            name="fax"
                            value={values.fax}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.fax && errors.fax
                                ? String(errors.fax)
                                : undefined
                            }
                            touched={!!touched.fax}
                            placeHolder="Enter fax number"
                            className="form-control"
                            maxLength={12}
                          />
                          <Label
                            className="form-label"
                            htmlFor="fax"
                            text="Fax Number"
                          />
                          {touched.fax && errors.fax && (
                            <div className="errorText">
                              {String(errors.fax)}
                            </div>
                          )}
                        </div>

                        {/* Email Address */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="email"
                            id="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.email && errors.email
                                ? String(errors.email)
                                : undefined
                            }
                            touched={!!touched.email}
                            placeHolder="Enter email address"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="email"
                            text="Email Address"
                          />
                          {touched.email && errors.email && (
                            <div className="errorText">
                              {String(errors.email)}
                            </div>
                          )}
                        </div>

                        {/* Password */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="password"
                            id="password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.password && errors.password
                                ? String(errors.password)
                                : undefined
                            }
                            touched={!!touched.password}
                            placeHolder="Create password"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="password"
                            text="Password"
                          />
                          {touched.password && errors.password && (
                            <div className="errorText">
                              {String(errors.password)}
                            </div>
                          )}
                        </div>

                        {/* PAN Number */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="panNumber"
                            name="panNumber"
                            value={values.panNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.panNumber && errors.panNumber
                                ? String(errors.panNumber)
                                : undefined
                            }
                            touched={!!touched.panNumber}
                            placeHolder="Enter PAN (e.g., ABCDE1234F)"
                            className="form-control"
                            maxLength={10}
                          />
                          <Label
                            className="form-label"
                            htmlFor="panNumber"
                            text="PAN Number"
                          />
                          {touched.panNumber && errors.panNumber && (
                            <div className="errorText">
                              {String(errors.panNumber)}
                            </div>
                          )}
                        </div>

                        {/* GST Number */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="gstNumber"
                            name="gstNumber"
                            value={values.gstNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.gstNumber && errors.gstNumber
                                ? String(errors.gstNumber)
                                : undefined
                            }
                            touched={!!touched.gstNumber}
                            placeHolder="Enter GSTIN (15 characters)"
                            className="form-control"
                            maxLength={15}
                          />
                          <Label
                            className="form-label"
                            htmlFor="gstNumber"
                            text="GST Number"
                          />
                          {touched.gstNumber && errors.gstNumber && (
                            <div className="errorText">
                              {String(errors.gstNumber)}
                            </div>
                          )}
                        </div>

                        {/* AXLPL Insurance Value */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="insuranceValue"
                            name="insuranceValue"
                            value={values.insuranceValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.insuranceValue && errors.insuranceValue
                                ? String(errors.insuranceValue)
                                : undefined
                            }
                            touched={!!touched.insuranceValue}
                            placeHolder="Enter AXLPL insurance amount"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="insuranceValue"
                            text="AXLPL Insurance Value"
                          />
                          {touched.insuranceValue && errors.insuranceValue && (
                            <div className="errorText">
                              {String(errors.insuranceValue)}
                            </div>
                          )}
                        </div>

                        {/* Third Party Insurance Value */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="text"
                            id="thirdPartyInsuranceValue"
                            name="thirdPartyInsuranceValue" // ✅ fixed name
                            value={values.thirdPartyInsuranceValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.thirdPartyInsuranceValue &&
                              errors.thirdPartyInsuranceValue
                                ? String(errors.thirdPartyInsuranceValue)
                                : undefined
                            }
                            touched={!!touched.thirdPartyInsuranceValue}
                            placeHolder="Enter third-party insurance amount"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="thirdPartyInsuranceValue"
                            text="Third Party Insurance Value"
                          />
                          {touched.thirdPartyInsuranceValue &&
                            errors.thirdPartyInsuranceValue && (
                              <div className="errorText">
                                {String(errors.thirdPartyInsuranceValue)}
                              </div>
                            )}
                        </div>

                        {/* Insurance Expiry Date */}
                        <div className="form-floating col-md-6 col-12 mb-5">
                          <Input
                            type="date"
                            id="insuranceExpiryDate"
                            name="insuranceExpiryDate"
                            value={values.insuranceExpiryDate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.insuranceExpiryDate &&
                              errors.insuranceExpiryDate
                                ? String(errors.insuranceExpiryDate)
                                : undefined
                            }
                            touched={!!touched.insuranceExpiryDate}
                            placeHolder="Select insurance expiry date"
                            className="form-control"
                          />
                          <Label
                            className="form-label"
                            htmlFor="insuranceExpiryDate"
                            text="Insurance Expiry Date"
                          />
                          {touched.insuranceExpiryDate &&
                            errors.insuranceExpiryDate && (
                              <div className="errorText">
                                {String(errors.insuranceExpiryDate)}
                              </div>
                            )}
                        </div>

                        {/* Upload PAN Card */}
                        <div className="col-md-6 col-12">
                          <DropzoneField
                            id="uploadPanCardInput"
                            name="uploadPanCard"
                            label="Upload PAN Card"
                            file={values.uploadPanCard}
                            accept={ACCEPTED_EXTENSIONS}
                            error={
                              touched.uploadPanCard && errors.uploadPanCard
                                ? String(errors.uploadPanCard)
                                : undefined
                            }
                            touched={
                              touched.uploadPanCard as unknown as boolean
                            }
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            setFieldError={setFieldError}
                          />
                        </div>

                        {/* Upload GST Certificate */}
                        <div className="col-md-6 col-12">
                          <DropzoneField
                            id="uploadGstCertificateInput"
                            name="uploadGstCertificate"
                            label="Upload GST Certificate"
                            file={values.uploadGstCertificate}
                            accept={ACCEPTED_EXTENSIONS}
                            error={
                              touched.uploadGstCertificate &&
                              errors.uploadGstCertificate
                                ? String(errors.uploadGstCertificate)
                                : undefined
                            }
                            touched={
                              touched.uploadGstCertificate as unknown as boolean
                            }
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                            setFieldError={setFieldError}
                          />
                        </div>
                      </div>

                      <div className="p_loginFormBtnsWrap custRegFormBtn">
                        <div className="row">
                          <div className="col-md-6">
                            <Button
                              text={loading ? "Processing..." : "Sign Up"}
                              type="button" // 👈 change from "submit"
                              className="btn authLoginBtn"
                              disabled={loading}
                              onClick={submitWithValidation} // 👈 run validation + submit
                            />
                            {loading && (
                              <div className="mt-2 text-center">
                                <InlineLogisticsLoader size="sm" />
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <Link to="/" className="btn authSignUpBtn">
                              Sign In
                            </Link>
                            <p className="mt-2 text-center fw-semibold">Already have an account?</p>
                          </div>
                        </div>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </section>
  );
};

export default Registration;
